import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, FileTextIcon, ShieldCheckIcon, SearchIcon, LightbulbIcon, SettingsIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { INTELLIGENCE_IMAGE, intelligenceCopy, intelligenceSteps, type Bi } from '../../data/animalsIntelligence';
import { IntelligenceMonitor } from './intelligence/IntelligenceDashboard';
import { PriorityCards, CompliancePanel, StatusPanel, RevenuePanel, RecommendationPanel } from './intelligence/IntelligencePanels';
const EASE = [0.23, 1, 0.32, 1] as const;
const STEP_ICONS = {
  understand: FileTextIcon,
  verify: ShieldCheckIcon,
  detect: SearchIcon,
  recommend: LightbulbIcon,
  act: SettingsIcon
} as const;
const PHOTO_MASK = {
  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 16%)',
  maskImage: 'linear-gradient(to right, transparent 0%, #000 16%)'
};
export function AnimalsIntelligence() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const c = intelligenceCopy;
  const reveal = {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    }
  };
  const message = <>
      <p className="font-sans text-[13px] font-bold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-291" data-mp="1q7oo3wayve9b">{pick(c.eyebrow)}</p>
      <h2 className="font-serif text-[2.6rem] sm:text-[3.4rem] 2xl:text-[4.3rem] leading-[1.02] tracking-tight mt-5" data-id="element-292" data-mp="1v2lwd10f9jl5">
        <span className="block text-[#121A3A]" data-id="element-293" data-mp="2ntcarphmei7n">{pick(c.line1)}</span>
        <span className="block text-[#4F46E5]" data-id="element-294" data-mp="2nrjdv1wt7ccu">{pick(c.line2)}</span>
      </h2>
      <p className="font-sans text-[18px] md:text-[19px] text-[#5B6478] leading-relaxed mt-6 max-w-[520px]" data-id="element-295" data-mp="28vtoctsf0h3d">{pick(c.body)}</p>
    </>;
  return <div className="relative w-full" data-id="element-296" data-mp="xhg3zywvejs3">
      {/* Desktop — monitor in a bright office */}
      <div className="hidden xl:block relative max-w-[1760px] mx-auto min-h-[820px]" data-id="element-297" data-mp="12g9ls3j6wwy2">
        <div className="absolute inset-y-0 right-0 w-[80%]" aria-hidden="true" data-id="element-298" data-mp="12dwu2ynmr87t">
          <img src={INTELLIGENCE_IMAGE} alt="" className="w-full h-full object-cover object-[0%_60%] opacity-90" style={PHOTO_MASK} data-id="element-299" data-mp="352546rpksgt5" />
        </div>
        <svg className="absolute bottom-0 left-0 w-full h-[190px] pointer-events-none z-20" viewBox="0 0 1440 190" preserveAspectRatio="none" aria-hidden="true" data-id="element-300" data-mp="2vclkjju1gix8">
          <path d="M0 190 L0 50 C 420 150, 1000 160, 1440 60 L1440 190 Z" fill="#FFFFFF" data-id="element-301" data-mp="2qbxw95x72g6p" />
        </svg>

        <motion.div {...reveal} transition={{
        duration: 0.3,
        ease: EASE
      }} className="relative z-10 w-[36%] pl-6 2xl:pl-10 pt-16" data-id="element-302" data-mp="2lcmdjrd9p0mh">
          {message}
        </motion.div>

        <motion.div {...reveal} transition={{
        duration: 0.3,
        delay: 0.1,
        ease: EASE
      }} className="absolute z-10 top-[36px] right-[1.5%] w-[60%] max-w-[1060px]" data-id="element-303" data-mp="2cesrrad4ppdk">
          <IntelligenceMonitor isFR={isFR} data-id="element-304" data-mp="ame5jz3jxyrc" />
        </motion.div>
      </div>

      {/* Tablet / mobile — panels stacked in priority order */}
      <div className="xl:hidden px-6 max-w-[1100px] mx-auto" data-id="element-305" data-mp="d7ltj0r5tm1r">
        <motion.div {...reveal} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-306" data-mp="d72djpl2l7qd">
          {message}
        </motion.div>
        <div className="mt-8 rounded-[24px] border border-[#EEEEF4] bg-[#FCFCFE] p-3 sm:p-4 space-y-3" data-id="element-307" data-mp="i42j7ze0c8gy">
          <PriorityCards isFR={isFR} data-id="element-308" data-mp="rw8kjzq5g28t" />
          <RecommendationPanel isFR={isFR} data-id="element-309" data-mp="rv6w8tk4hj95" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-310" data-mp="ryb5w6gcx777">
            <CompliancePanel isFR={isFR} data-id="element-311" data-mp="3qf5onxrgo7g1" />
            <StatusPanel isFR={isFR} data-id="element-312" data-mp="3qg8ctxxefvs9" />
            <RevenuePanel isFR={isFR} data-id="element-313" data-mp="3vc5nbnlze7x4" />
          </div>
        </div>
      </div>

      {/* Five-step intelligence sequence */}
      <div className="relative z-30 bg-white xl:bg-transparent xl:-mt-6" data-id="element-314" data-mp="452uysuepvlb">
        <ol className="max-w-[1760px] mx-auto px-6 2xl:px-10 pt-12 xl:pt-0 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr_auto_1fr] gap-6 xl:gap-4 items-start" data-id="element-315" data-mp="96s608hmq99e">
          {intelligenceSteps.map((s, i) => {
          const Icon = STEP_ICONS[s.key];
          return <Fragment key={s.key} data-id="element-316" data-mp="1i7aa2msmlro6" data-mpi={i}>
                <li className="flex items-start gap-4" data-id="element-317" data-mp="1i8bpf3god0bp">
                  <span className="w-[72px] h-[72px] rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-318" data-mp="1e5p1hdn4jzjs">
                    <Icon className="w-8 h-8 text-[#4F46E5]" strokeWidth={1.6} data-id="element-319" data-mp="1s2c33tq0vdw1" />
                  </span>
                  <span className="min-w-0 pt-2" data-id="element-320" data-mp="1n7ojo99vl9ob">
                    <span className="block font-sans text-[16px] font-bold uppercase tracking-wide text-[#121A3A]" data-id="element-321" data-mp="2141syv49a4rp">{pick(s.title)}</span>
                    <span className="block font-sans text-[15px] text-[#5B6478] leading-snug mt-1" data-id="element-322" data-mp="1x1f515aph3zs">{pick(s.body)}</span>
                  </span>
                </li>
                {i < intelligenceSteps.length - 1 && <li className="hidden xl:flex items-center h-[72px]" aria-hidden="true" data-id="element-323" data-mp="2kt3umdw57ed1">
                    <ArrowRightIcon className="w-5 h-5 text-[#A5A0F2]" strokeWidth={1.8} data-id="element-324" data-mp="2kqq2odwrei9p" />
                  </li>}
              </Fragment>;
        })}
        </ol>
      </div>
    </div>;
}